/**
 * ScreenReaderAnnouncer - Aria-live region for screen reader announcements
 * Provides a central place to announce dynamic changes to assistive technology.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  AnnouncerContextValue,
  ScreenReaderAnnouncerProps,
} from './ScreenReaderAnnouncer.types';

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null);

export function ScreenReaderAnnouncer({
  children,
}: ScreenReaderAnnouncerProps): React.JSX.Element {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  // Store RAF IDs for cleanup
  const politeRafRef = useRef<number | null>(null);
  const assertiveRafRef = useRef<number | null>(null);

  // Cancel pending RAF on unmount
  useEffect(() => {
    return () => {
      if (politeRafRef.current !== null) {
        cancelAnimationFrame(politeRafRef.current);
      }
      if (assertiveRafRef.current !== null) {
        cancelAnimationFrame(assertiveRafRef.current);
      }
    };
  }, []);

  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (priority === 'assertive') {
        // Cancel any pending assertive RAF
        if (assertiveRafRef.current !== null) {
          cancelAnimationFrame(assertiveRafRef.current);
        }
        setAssertiveMessage('');
        // Clear then set to trigger re-announcement of same message
        assertiveRafRef.current = requestAnimationFrame(() => {
          setAssertiveMessage(message);
          assertiveRafRef.current = null;
        });
      } else {
        // Cancel any pending polite RAF
        if (politeRafRef.current !== null) {
          cancelAnimationFrame(politeRafRef.current);
        }
        setPoliteMessage('');
        politeRafRef.current = requestAnimationFrame(() => {
          setPoliteMessage(message);
          politeRafRef.current = null;
        });
      }
    },
    []
  );

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      {/* Polite announcements - for non-urgent updates */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      {/* Assertive announcements - for important/urgent updates */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
}

/**
 * Hook to access the screen reader announcer
 */
export function useScreenReaderAnnouncer(): AnnouncerContextValue {
  const context = useContext(AnnouncerContext);
  if (!context) {
    // Return a no-op announcer if not wrapped in provider
    return {
      announce: () => {},
    };
  }
  return context;
}

export default ScreenReaderAnnouncer;
